// Journey Lab's client-side wire contract and same-origin HTTP transport. This is the ONE browser owner for
// the catalogue, replay admission, and run-detail shapes. The server owns runtime validation and actor scope;
// the browser sends no Actor, role, execution-mode override, file path, or scenario body. A replay is always
// admitted by the server as the explicitly returned execution mode, and this transport never retries it.

import { API_PREFIX } from './post-intent.ts';
import type {
  JourneyCatalogueView,
  JourneyReplayReceiptView,
  JourneyRunDetailView,
} from '../../../api/journey-wire.ts';

// WIRE_SSOT: app/modules/dashboard/api/journey-wire.ts
export type {
  JourneyAssertionView,
  JourneyCatalogueItemView as JourneyCatalogueItem,
  JourneyCatalogueView,
  JourneyEvidenceView,
  JourneyExecutionMode,
  JourneyLiveExecutionMode,
  JourneyModelVerification,
  JourneyReplayIntent,
  JourneyReplayReceiptView as JourneyReplayAccepted,
  JourneyRunDetailView as JourneyRunEnvelope,
  JourneyRunState,
  JourneyRunSummaryView as JourneyLatestRunView,
  JourneyRunView,
  JourneySource,
  JourneyStepState,
  JourneyStepView,
  JourneyVerdict,
} from '../../../api/journey-wire.ts';

export interface JourneyLabApi {
  readonly getJourneys: () => Promise<JourneyCatalogueView>;
  readonly getJourneyRun: (runId: string) => Promise<JourneyRunDetailView>;
  readonly replayJourney: (scenarioId: string) => Promise<JourneyReplayReceiptView>;
}

const JOURNEYS_PATH = `${API_PREFIX}/journeys`;
const journeyRunPath = (runId: string): string =>
  `${JOURNEYS_PATH}/runs/${encodeURIComponent(runId)}`;
const journeyReplayPath = (scenarioId: string): string =>
  `${JOURNEYS_PATH}/${encodeURIComponent(scenarioId)}/replay`;

async function readJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`journey read failed (${String(response.status)})`);
  return (await response.json()) as T;
}

async function postReplay(scenarioId: string): Promise<JourneyReplayReceiptView> {
  const response = await fetch(journeyReplayPath(scenarioId), {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ executionMode: 'scripted' }),
  });
  if (!response.ok) throw new Error(`journey replay failed (${String(response.status)})`);
  return (await response.json()) as JourneyReplayReceiptView;
}

/** The real Journey Lab transport. Every call stays on the authenticated dashboard origin. */
export function httpJourneyLabApi(): JourneyLabApi {
  return {
    getJourneys: () => readJson<JourneyCatalogueView>(JOURNEYS_PATH),
    getJourneyRun: (runId) => readJson<JourneyRunDetailView>(journeyRunPath(runId)),
    replayJourney: postReplay,
  };
}
